import { isInitialContextSelectionRange } from '../config-items/config-items';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
export function streamingService(context, event) {
  function callbackHandler(callback) {
    var _configItemToUse$getI, _configItemToUse$getB;
    const abortController = new AbortController();

    /**
     * Vary prompt request triggered - if latest response exists, we are interrogating,
     * and do not want to use the original config item (i.e. may possibly be summarise/transform)
     *
     * If it is a generate with tag, the response will be captured within
     * the latest "latest response, prompt", flow.
     *
     * REVISIT THIS: analytics is based on the config item, however
     * interrogation analytics uses base generate instead to complete;
     */
    const configItemToUse = context.baseGenerate && context.latestPromptResponse ? context.baseGenerate : context.configItem;
    const initialContext = (_configItemToUse$getI = configItemToUse.getInitialContext) === null || _configItemToUse$getI === void 0 ? void 0 : _configItemToUse$getI.call(configItemToUse, {
      editorView: context.editorView,
      positions: context.positions,
      intl: context.intl,
      updateIdMap: ({
        idMap,
        selectionType
      }) => {
        callback({
          type: 'update idMap',
          context: {
            idMap,
            selectionType
          }
        });
      }
    });
    if (initialContext !== null && initialContext !== void 0 && initialContext.contentStatistics) {
      callback({
        type: 'storeContextStatistics',
        contextStatistics: {
          inputDocument: initialContext.contentStatistics
        }
      });
    }
    if (!initialContext) {
      throw new Error('Initial context is undefined');
    }
    const promptRequest = configItemToUse.triggerPromptRequest({
      initialContext,
      promptInput: context.userInput,
      promptInputADF: context.userADFInput,
      latestPromptResponse: context.latestPromptResponse || undefined,
      formatMessage: context.intl.formatMessage,
      editorSchema: context.editorView.state.schema,
      backendModel: configItemToUse === null || configItemToUse === void 0 ? void 0 : (_configItemToUse$getB = configItemToUse.getBackendModel) === null || _configItemToUse$getB === void 0 ? void 0 : _configItemToUse$getB.call(configItemToUse, !!context.latestPromptResponse)
    });
    const cacheKey = JSON.stringify({
      initialContext,
      userInput: context.userInput
    });

    // Right now we only ever have one object in history as we do not
    // yet support traversing the history object array. This is also to
    // prevent us saving large documents into history multip[le times.
    const lastHistoryItem = context.responseHistory.entries[0];
    if (getBooleanFF('platform.editor.ai-save-response-history-in-state_dw857')) {
      if (lastHistoryItem && cacheKey === lastHistoryItem.cacheKey) {
        callback({
          type: 'same cache key'
        });
        return;
      }
    }
    const streaming = promptRequest({
      abortController,
      // Note -- when we collapse to a single "prompt request" with "api v2"
      // this would be a good opportunity to group these and pass them as a group
      generativeAIApiUrl: context.aiProvider.generativeAIApiUrl,
      product: context.aiProvider.product,
      getFetchCustomHeaders: context.aiProvider.getFetchCustomHeaders,
      getChannelVariables: context.aiProvider.getChannelVariables
    });
    (async function () {
      for await (const streamItem of streaming) {
        if (streamItem.state === 'loaded') {
          var _streamItem$data$meta;
          //loading done on success
          callback({
            type: 'complete',
            markdown: streamItem.data.content,
            cacheKey,
            modelInput: {
              selection: isInitialContextSelectionRange(initialContext) ? initialContext.selection : undefined
            },
            inputOutputDiffRatio: (_streamItem$data$meta = streamItem.data.meta) === null || _streamItem$data$meta === void 0 ? void 0 : _streamItem$data$meta.inputOutputDiffRatio
          });
          continue;
        } else if (streamItem.state === 'failed') {
          //loading done on api failed

          // If the error comes from ConvoAI
          if ('apiName' in streamItem && streamItem.apiName === 'assistance-service') {
            if (['RATE_LIMIT', 'OPENAI_RATE_LIMIT_USER_ABUSE'].includes(streamItem.guard) && 'retryAfter' in streamItem) {
              callback({
                type: 'rate limited',
                retryAfter: streamItem.retryAfter,
                errorInfo: {
                  statusCode: streamItem.statusCode,
                  errorKey: streamItem.guard,
                  apiName: streamItem.apiName,
                  errorContent: 'error' in streamItem ? streamItem.error : undefined
                }
              });
              continue;
            }
            if (streamItem.guard === 'HIPAA_CONTENT_DETECTED') {
              callback({
                type: 'hipaa content fail',
                errorInfo: {
                  statusCode: streamItem.statusCode,
                  errorKey: streamItem.guard,
                  apiName: streamItem.apiName,
                  errorContent: 'error' in streamItem ? streamItem.error : undefined
                }
              });
              continue;
            }
            if (streamItem.guard === 'EXCEEDING_CONTEXT_LENGTH_ERROR') {
              callback({
                type: 'token limit fail',
                errorInfo: {
                  statusCode: streamItem.statusCode,
                  errorKey: streamItem.guard,
                  apiName: streamItem.apiName,
                  errorContent: 'error' in streamItem ? streamItem.error : undefined
                }
              });
              continue;
            }
            if (streamItem.guard === 'ACCEPTABLE_USE_VIOLATIONS') {
              callback({
                type: 'AUP violation detected',
                cacheKey,
                modelInput: {
                  selection: isInitialContextSelectionRange(initialContext) ? initialContext.selection : undefined
                },
                errorInfo: {
                  statusCode: streamItem.statusCode,
                  errorKey: streamItem.guard,
                  apiName: streamItem.apiName,
                  errorContent: 'error' in streamItem ? streamItem.error : undefined
                }
              });
              continue;
            }

            // All other errors from ConvoAI
            callback({
              type: 'api fail',
              errorInfo: {
                statusCode: streamItem.statusCode,
                errorKey: streamItem.guard,
                apiName: streamItem.apiName,
                errorContent: 'error' in streamItem ? streamItem.error : undefined
              }
            });
            continue;
          }
          if ('reason' in streamItem && streamItem.reason === 'backend-input-guard') {
            if (streamItem.guard === 'INPUT_EXCEEDS_TOKEN_LIMIT') {
              callback({
                type: 'token limit fail',
                errorInfo: {
                  errorKey: streamItem.guard,
                  statusCode: streamItem.statusCode
                }
              });
              continue;
            }
            if (streamItem.guard === 'INPUT_TOO_SHORT_TO_SUMMARIZE' || streamItem.guard === 'INPUT_TOO_SHORT_TO_PROCESS') {
              callback({
                type: 'input too short fail',
                errorInfo: {
                  errorKey: streamItem.guard,
                  statusCode: streamItem.statusCode
                }
              });
              continue;
            }
          }
          if ('reason' in streamItem && streamItem.reason === 'rate-limited') {
            callback({
              type: 'rate limited',
              retryAfter: streamItem.retryAfter
            });
            continue;
          }
          if ('reason' in streamItem && streamItem.reason === 'response-too-similar') {
            var _streamItem$data$meta2;
            callback({
              type: 'response too similar',
              cacheKey,
              modelInput: {
                selection: isInitialContextSelectionRange(initialContext) ? initialContext.selection : undefined
              },
              inputOutputDiffRatio: (_streamItem$data$meta2 = streamItem.data.meta) === null || _streamItem$data$meta2 === void 0 ? void 0 : _streamItem$data$meta2.inputOutputDiffRatio
            });
            continue;
          }
          callback({
            type: 'api fail',
            errorInfo: {
              statusCode: streamItem.statusCode
            }
          });
        } else if (streamItem.state === 'created-channel') {
          callback({
            type: 'update channelId',
            context: {
              channelId: streamItem.data.channelId
            }
          });
        } else if (streamItem.state === 'aup-violation') {
          callback({
            type: 'AUP violation detected',
            cacheKey,
            modelInput: {
              selection: isInitialContextSelectionRange(initialContext) ? initialContext.selection : undefined
            }
          });
          continue;
        } else {
          var _streamItem$data$meta3;
          callback({
            type: 'stream',
            markdown: streamItem.data.content,
            loadingStatus: (_streamItem$data$meta3 = streamItem.data.meta) === null || _streamItem$data$meta3 === void 0 ? void 0 : _streamItem$data$meta3.loadingStatus
          });
          continue;
        }
      }
    })();

    // Cleanup function
    return () => {
      abortController.abort();
    };
  }
  return callbackHandler;
}